import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield } from 'lucide-react';
import { Header } from "@/components/header";
import { Footer } from '@/components/footer';
import { CTA } from '@/components/cta';
import { FAQ } from '@/components/faq';


export default function SecurityBenefit() {
  const { t } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <Header />
      <div className="container mx-auto px-4 py-16">        
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-slate-100 dark:bg-slate-900/30 w-16 h-16 rounded-2xl flex items-center justify-center text-slate-600 dark:text-slate-400">
              <Shield className="w-8 h-8" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              {t('benefitsCloud.benefits.4.title')}
            </h1>
          </div>
          
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-xl text-slate-600 dark:text-slate-300 mb-8">
              {t('benefitsCloud.benefits.4.description')}
            </p>
            
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                {t('benefitsCloud.details.security.title', 'Enterprise-Grade Security')}
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
                {t('benefitsCloud.details.security.description', 'Our cloud-based security solution provides military-grade encryption and advanced protection measures to keep your data and premises secure against modern threats.')}
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 mt-12">
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.security.benefit1', 'End-to-End Encryption')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.security.benefit1Desc', 'All data is encrypted in transit and at rest using industry-standard protocols.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.security.benefit2', 'Multi-Factor Authentication')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.security.benefit2Desc', 'Add an extra layer of security to prevent unauthorized access.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.security.benefit3', 'Regular Security Audits')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.security.benefit3Desc', 'Continuous monitoring and regular security assessments to ensure compliance.')}
                  </p>
                </div>
                
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 dark:hover:border-slate-600">
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                    {t('benefitsCloud.details.security.benefit4', 'Data Redundancy')}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300">
                    {t('benefitsCloud.details.security.benefit4Desc', 'Multiple backups ensure your data is always available and protected.')}
                  </p>
                </div>
              </div>
              
              <FAQ 
                    faqs={t('benefitsCloud.details.security.faq', { returnObjects: true }) as Array<{id?: string, question: string, answer: string}>} 
                    title={t('faq.title', 'Frequently Asked Questions')}
                    subtitle={t('benefitsCloud.details.security.faqSubtitle', 'Everything you need to know about security')}
                />
            </div>
          </div>
        </div>
      </div>
      
      <CTA />
      <Footer />
    </div>
  );
}
